import { useEffect, useRef } from 'react';
import { useDisclaimerBlocking } from '../common/useDisclaimerBlocking';
import { useGeoItemUrlParam } from './useGeoItemUrlParam';

/** What reconciling the URL against the open record should do next. */
export type DeepLinkAction =
  | { type: 'none' }
  | { type: 'wait' }
  | { type: 'open'; id: string }
  | { type: 'close' }
  | { type: 'notFound'; id: string }
  | { type: 'writeUrl'; id: string | null };

type ResolveInput = {
  /** `?geoItem=` as it currently reads. */
  urlId: string | null;
  /** The record this surface currently has open. */
  openId: string | null;
  /**
   * `urlId` the last time this ran, so a URL change can be told from a state change. `undefined`
   * before the first run, so on mount the URL wins over whatever the store still holds.
   */
  lastUrlId: string | null | undefined;
  /** An id already reported as not in this project. */
  unresolvedId: string | null;
  has: (id: string) => boolean;
  /** True while a modal the user must answer first is on screen. */
  blocked: boolean;
};

/**
 * Decides which side won. Whichever of the URL and the open record changed is the one that
 * propagates, which is what keeps Back/Forward from being fought by local state and keeps the two
 * effects from writing each other in a loop.
 *
 * Pure so the reconciliation can be tested without rendering.
 */
export function resolveDeepLinkAction({
  urlId,
  openId,
  lastUrlId,
  unresolvedId,
  has,
  blocked,
}: ResolveInput): DeepLinkAction {
  // Nothing may be decided while blocked, not even writing the URL: the caller returns before
  // `lastUrlId` advances, which is what leaves the link intact to resolve once the way is clear.
  if (blocked) return { type: 'wait' };

  if (urlId === openId) return { type: 'none' };

  if (urlId !== lastUrlId) {
    if (!urlId) return { type: 'close' };
    return has(urlId) ? { type: 'open', id: urlId } : { type: 'notFound', id: urlId };
  }

  // The record changed. While nothing is open, leave an unresolved id in the URL rather than
  // rewriting what was sent; a record opened by hand takes the URL over as usual.
  if (urlId && urlId === unresolvedId && !openId) return { type: 'none' };
  return { type: 'writeUrl', id: openId };
}

type GeoItemDeepLinkOptions = {
  /** False while the surface's records are still loading — a link must not resolve against an empty list. */
  ready: boolean;
  openId: string | null;
  has: (id: string) => boolean;
  onOpen: (id: string) => void;
  onClose: () => void;
  /** An id the URL names but this surface does not hold. Fires once per id. */
  onNotFound: (id: string) => void;
};

/**
 * Keeps `?geoItem=` and the open record in step, in both directions: an exported `civilgridUrl`
 * (or Back/Forward) opens the record it names, and opening one by hand puts it in the URL.
 */
export function useGeoItemDeepLink({ ready, openId, has, onOpen, onClose, onNotFound }: GeoItemDeepLinkOptions) {
  const { geoItemId, setGeoItemId } = useGeoItemUrlParam();
  // The Disclaimer modal must be answered before a link opens a record over it; a second Radix
  // modal would also take the pointer lock and leave its Accept button unclickable.
  const disclaimerBlocking = useDisclaimerBlocking();
  const lastUrlId = useRef<string | null | undefined>(undefined);
  const unresolvedId = useRef<string | null>(null);

  useEffect(() => {
    if (!ready) return;

    const action = resolveDeepLinkAction({
      urlId: geoItemId,
      openId,
      lastUrlId: lastUrlId.current,
      unresolvedId: unresolvedId.current,
      has,
      blocked: disclaimerBlocking,
    });

    if (action.type === 'wait') return;

    if (action.type !== 'writeUrl') lastUrlId.current = geoItemId;

    switch (action.type) {
      case 'open':
        onOpen(action.id);
        break;
      case 'close':
        onClose();
        break;
      case 'notFound':
        unresolvedId.current = action.id;
        onNotFound(action.id);
        break;
      case 'writeUrl':
        unresolvedId.current = null;
        setGeoItemId(action.id);
        break;
    }
  }, [ready, disclaimerBlocking, geoItemId, openId, has, onOpen, onClose, onNotFound, setGeoItemId]);
}
